/**
 * SkuPicker — horizontally-scrollable product card carousel.
 * Replaces the old dropdown SKU selector. Each SKU is a 140×140 tile with
 * product image + name + category. Scrolls horizontally with mouse-wheel /
 * drag / arrow buttons.
 */
import React, { useRef } from 'react';
import { Check, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { T, BG, BORDER } from './constants';

export default function SkuPicker({ skus, selectedSkuId, onSelect }) {
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  if (!skus || skus.length === 0) {
    return (
      <div className="p-6 rounded-lg text-center text-[11px] text-gray-500" style={{ background: BG, border: `1px dashed ${BORDER}` }}>
        No products in your catalog yet. Add SKUs in Brand Profile to pick one here.
      </div>
    );
  }

  return (
    <div className="relative group" data-testid="sku-picker">
      {/* Left arrow */}
      <button type="button" onClick={() => scroll(-1)}
              aria-label="Scroll SKUs left" data-testid="sku-scroll-left"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: '#121212', border: `1px solid ${BORDER}`, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
        <ChevronLeft className="h-4 w-4 text-white" />
      </button>

      {/* Scrollable row */}
      <div ref={scrollerRef}
           className="flex gap-3 overflow-x-auto scroll-smooth pb-2 snap-x snap-mandatory scrollbar-hide"
           style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
           data-testid="sku-scroller">
        {skus.map(sku => {
          const active = sku.id === selectedSkuId;
          return (
            <button key={sku.id} type="button" onClick={() => onSelect(sku.id)}
                    data-testid={`sku-tile-${sku.id}`}
                    className="flex-shrink-0 snap-start w-36 rounded-xl overflow-hidden transition-all duration-200 group/tile relative"
                    style={{
                      background: BG,
                      border: `2px solid ${active ? T : BORDER}`,
                      boxShadow: active ? `0 0 24px ${T}40` : 'none',
                      transform: active ? 'translateY(-2px)' : 'translateY(0)',
                    }}>
              <div className="w-full h-24 overflow-hidden relative">
                {sku.image ? (
                  <img src={sku.image} alt={sku.name} className="w-full h-full object-cover transition-transform group-hover/tile:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: '#0f0f0f' }}>
                    <Package className="h-6 w-6 text-gray-700" />
                  </div>
                )}
                {active && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                       style={{ background: T, boxShadow: `0 0 12px ${T}` }}>
                    <Check className="h-3 w-3 text-black" />
                  </div>
                )}
              </div>
              <div className="p-2.5">
                <p className="text-[10px] font-bold text-white leading-tight line-clamp-2" style={{ minHeight: '2.2em' }}>{sku.name}</p>
                {sku.category && (
                  <p className="text-[8px] text-gray-600 mt-1 uppercase tracking-wider">{sku.category}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Right arrow */}
      <button type="button" onClick={() => scroll(1)}
              aria-label="Scroll SKUs right" data-testid="sku-scroll-right"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: '#121212', border: `1px solid ${BORDER}`, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
        <ChevronRight className="h-4 w-4 text-white" />
      </button>

      {/* Inline style to hide scrollbar cross-browser */}
      <style>{`
        [data-testid="sku-scroller"]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
